import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Search,
  SlidersHorizontal,
  MapPin,
  Star,
  Heart,
  CalendarIcon,
  X,
  Wifi,
  Car,
  Coffee,
  Music,
  Camera,
  Tv,
  Inbox,
  BadgeCheck,
} from "lucide-react";

import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useFavorite } from "@/lib/favorites-store";

import s1 from "@/assets/space-1.webp";
import s2 from "@/assets/space-2.webp";
import s3 from "@/assets/space-3.webp";
import s4 from "@/assets/space-4.webp";
import s5 from "@/assets/space-5.webp";
import { Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/discover")({
  head: () => ({
    meta: [
      { title: "Discover spaces — Spayzo" },
      { name: "description", content: "Browse and filter hourly-bookable spaces by price, type, time and amenities across Saudi Arabia." },
      { property: "og:title", content: "Discover spaces — Spayzo" },
      { property: "og:description", content: "Find the perfect space for your next session, shoot, or meeting." },
      { property: "og:url", content: "https://spayzo.lovable.app/discover" },
    ],
    links: [{ rel: "canonical", href: "https://spayzo.lovable.app/discover" }],
  }),
  component: DiscoverPage,
});

type SpaceType =
  | "Shared Office"
  | "Private Office"
  | "Meeting Room"
  | "Conference Room"
  | "Coworking Space"
  | "Training Room"
  | "Hot Desk";


interface Space {
  id: string;
  img: string;
  title: string;
  city: string;
  price: number;
  rating: number;
  reviews: number;
  type: SpaceType;
  amenities: string[];
  tags: string[];
  popularity: number;
  subscription?: boolean;
}

const ALL_SPACES: Space[] = [
  { id: "1", img: s1, title: "Industrial loft coworking", city: "Brooklyn, NY", price: 64, rating: 4.92, reviews: 128, type: "Coworking Space", amenities: ["Wi-Fi", "Parking", "Coffee"], tags: ["Natural light", "Pet friendly"], popularity: 92, subscription: true },
  { id: "2", img: s2, title: "Focused hot desk pod", city: "Austin, TX", price: 38, rating: 4.87, reviews: 94, type: "Hot Desk", amenities: ["Wi-Fi", "Sound", "Coffee"], tags: ["Quiet", "Cozy"], popularity: 78 },
  { id: "3", img: s3, title: "Skyline training room", city: "Lisbon, PT", price: 95, rating: 5.0, reviews: 41, type: "Training Room", amenities: ["Wi-Fi", "Parking", "Sound"], tags: ["Projector", "View"], popularity: 88, subscription: true },
  { id: "4", img: s4, title: "Velvet lounge meeting room", city: "Paris, FR", price: 52, rating: 4.95, reviews: 212, type: "Meeting Room", amenities: ["Wi-Fi", "Coffee", "TV"], tags: ["Cozy", "Central"], popularity: 95, subscription: true },
  { id: "5", img: s5, title: "Executive conference suite", city: "Berlin, DE", price: 78, rating: 4.89, reviews: 67, type: "Conference Room", amenities: ["Wi-Fi", "Parking"], tags: ["High ceilings"], popularity: 71 },
  { id: "6", img: s1, title: "Sun-drenched private office", city: "Miami, FL", price: 42, rating: 4.8, reviews: 53, type: "Private Office", amenities: ["Wi-Fi", "Coffee", "Camera"], tags: ["Natural light"], popularity: 64, subscription: true },
  { id: "7", img: s3, title: "Team shared office", city: "Chamonix, FR", price: 120, rating: 4.97, reviews: 38, type: "Shared Office", amenities: ["Wi-Fi", "Parking", "TV"], tags: ["Mountain view"], popularity: 82 },
  { id: "8", img: s2, title: "Boardroom with cyclorama", city: "London, UK", price: 88, rating: 4.91, reviews: 144, type: "Conference Room", amenities: ["Wi-Fi", "Camera", "Sound"], tags: ["Pro gear"], popularity: 90, subscription: true },
  { id: "9", img: s5, title: "Lakeside training pavilion", city: "Zurich, CH", price: 150, rating: 4.93, reviews: 29, type: "Training Room", amenities: ["Wi-Fi", "Parking", "Sound", "Coffee"], tags: ["Waterfront"], popularity: 70 },
];

const CITIES = [
  "Riyadh",
  "Jeddah",
  "Mecca",
  "Medina",
  "Dammam",
  "Khobar",
  "Dhahran",
  "Taif",
  "Tabuk",
  "Abha",
];

const SPACE_TYPES: SpaceType[] = [
  "Shared Office",
  "Private Office",
  "Meeting Room",
  "Conference Room",
  "Coworking Space",
  "Training Room",
  "Hot Desk",
];

const AMENITIES = [
  { name: "Wi-Fi", icon: Wifi },
  { name: "Parking", icon: Car },
  { name: "Coffee", icon: Coffee },
  { name: "Sound", icon: Music },
  { name: "Camera", icon: Camera },
  { name: "TV", icon: Tv },
];

type SortKey = "popular" | "price-asc" | "price-desc" | "rating";

function DiscoverPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [price, setPrice] = useState<[number, number]>([20, 200]);
  const [types, setTypes] = useState<SpaceType[]>([]);
  const [amenities, setAmenities] = useState<string[]>([]);
  const [city, setCity] = useState<string>("all");
  const [date, setDate] = useState<Date | undefined>();
  const [time, setTime] = useState<string>("any");
  const [sort, setSort] = useState<SortKey>("popular");
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 700);
    return () => clearTimeout(t);
  }, []);

  const toggle = <T,>(arr: T[], v: T) =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = ALL_SPACES.filter((s) => {
      if (s.price < price[0] || s.price > price[1]) return false;
      if (types.length && !types.includes(s.type)) return false;
      if (amenities.length && !amenities.every((a) => s.amenities.includes(a))) return false;
      if (city !== "all" && s.city !== city) return false;
      if (q && !(s.title.toLowerCase().includes(q) || s.city.toLowerCase().includes(q))) return false;
      return true;
    });
    out = [...out].sort((a, b) => {
      switch (sort) {
        case "price-asc": return a.price - b.price;
        case "price-desc": return b.price - a.price;
        case "rating": return b.rating - a.rating;
        default: return b.popularity - a.popularity;
      }
    });
    return out;
  }, [query, price, types, amenities, city, sort]);

  const activeCount = types.length + amenities.length + (city !== "all" ? 1 : 0) + (date ? 1 : 0);

  const resetFilters = () => {
    setPrice([20, 200]);
    setTypes([]);
    setAmenities([]);
    setCity("all");
    setDate(undefined);
    setTime("any");
  };

  const FiltersPanel = (
    <div className="space-y-8">
      <FilterSection title={t("discover.pricePerHour")} hint={`SAR ${price[0]} – SAR ${price[1]}`}>
        <Slider
          aria-label={t("discover.pricePerHour")}
          value={price}
          onValueChange={(v) => setPrice([v[0], v[1]] as [number, number])}
          min={10}
          max={300}
          step={5}
          className="mt-2"
        />
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{t("discover.min")}</label>
            <Input
              aria-label={t("discover.min")}
              type="number"
              min={10}
              max={price[1]}
              value={price[0]}
              onChange={(e) => {
                const v = Math.min(Math.max(Number(e.target.value), 10), price[1]);
                setPrice([v, price[1]]);
              }}
              className="h-9 rounded-xl text-sm"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{t("discover.max")}</label>
            <Input
              aria-label={t("discover.max")}
              type="number"
              min={price[0]}
              max={300}
              value={price[1]}
              onChange={(e) => {
                const v = Math.max(Math.min(Number(e.target.value), 300), price[0]);
                setPrice([price[0], v]);
              }}
              className="h-9 rounded-xl text-sm"
            />
          </div>
        </div>
      </FilterSection>

      <FilterSection title={t("discover.spaceType")}>
        <div className="grid grid-cols-2 gap-2">
          {SPACE_TYPES.map((ty) => {
            const active = types.includes(ty);
            return (
              <button
                key={ty}
                onClick={() => setTypes(toggle(types, ty))}
                className={cn(
                  "rounded-xl border px-3 py-2.5 text-start text-sm transition-all",
                  active
                    ? "border-foreground bg-foreground text-background"
                    : "border-border bg-card hover:border-foreground/40"
                )}
              >
                {t(`discover.types.${ty}`)}
              </button>
            );
          })}
        </div>
      </FilterSection>

      <FilterSection title={t("discover.cityLabel")}>
        <Select value={city} onValueChange={setCity}>
          <SelectTrigger className="rounded-xl">
            <MapPin className="me-2 h-4 w-4 text-muted-foreground" />
            <SelectValue placeholder={t("discover.allCities")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("discover.allCities")}</SelectItem>
            {CITIES.map((c) => (
              <SelectItem key={c} value={c}>{t(`discover.cities.${c}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title={t("discover.dateTime")}>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start rounded-xl">
              <CalendarIcon className="me-2 h-4 w-4" />
              {date ? format(date, "EEE, MMM d") : t("discover.pickDate")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>
        <Select value={time} onValueChange={setTime}>
          <SelectTrigger className="mt-2 rounded-xl">
            <SelectValue placeholder={t("discover.anyTime")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{t("discover.anyTime")}</SelectItem>
            <SelectItem value="morning">{t("discover.morning")}</SelectItem>
            <SelectItem value="afternoon">{t("discover.afternoon")}</SelectItem>
            <SelectItem value="evening">{t("discover.evening")}</SelectItem>
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title={t("discover.amenitiesLabel")}>
        <div className="space-y-2.5">
          {AMENITIES.map(({ name, icon: Icon }) => {
            const active = amenities.includes(name);
            return (
              <label
                key={name}
                className="flex cursor-pointer items-center gap-3 rounded-lg px-1 py-1 text-sm hover:bg-muted/60"
              >
                <Checkbox
                  checked={active}
                  onCheckedChange={() => setAmenities(toggle(amenities, name))}
                />
                <Icon className="h-4 w-4 text-muted-foreground" />
                <span>{t(`discover.amenityNames.${name}`)}</span>
              </label>
            );
          })}
        </div>
      </FilterSection>

      <Button variant="ghost" onClick={resetFilters} className="w-full rounded-xl">
        <X className="me-2 h-4 w-4" /> {t("discover.clearAll")}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {/* Top bar */}
      <section aria-label={t("discover.title")} className="sticky top-16 z-30 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
          <div className="relative flex flex-1 items-center">
            <Search className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label={t("discover.searchPlaceholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("discover.searchPlaceholder")}
              className="h-12 rounded-full border-border bg-card ps-11 pe-12 shadow-soft"
            />
            <Button
              type="button"
              size="icon"
              className="absolute end-1.5 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full bg-[#5958F3] text-white hover:bg-[#5958F3]/90"
            >
              <Search className="h-4 w-4" />
              <span className="sr-only">{t("discover.search")}</span>
            </Button>
          </div>


          <div className="flex items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="rounded-full lg:hidden">
                  <SlidersHorizontal className="me-2 h-4 w-4" />
                  {t("discover.filters")} {activeCount > 0 && <span className="ms-1 rounded-full bg-foreground px-1.5 text-[11px] text-background">{activeCount}</span>}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[88vw] max-w-sm overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>{t("discover.filters")}</SheetTitle>
                </SheetHeader>
                <div className="mt-6">{FiltersPanel}</div>
              </SheetContent>
            </Sheet>

            <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
              <SelectTrigger className="h-11 w-[170px] rounded-full bg-card">
                <SelectValue placeholder={t("discover.sortLabel")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">{t("discover.sortPopular")}</SelectItem>
                <SelectItem value="price-asc">{t("discover.sortPriceAsc")}</SelectItem>
                <SelectItem value="price-desc">{t("discover.sortPriceDesc")}</SelectItem>
                <SelectItem value="rating">{t("discover.sortTopRated")}</SelectItem>
              </SelectContent>
            </Select>

          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[280px_1fr] lg:px-8">
        {/* Sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-40 max-h-[calc(100vh-11rem)] overflow-y-auto rounded-3xl border border-border bg-card p-6 shadow-soft">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">{t("discover.filters")}</h2>
              {activeCount > 0 && (
                <span className="rounded-full bg-primary-soft px-2 py-0.5 text-xs font-medium text-accent-foreground">
                  {activeCount} {t("discover.active")}
                </span>
              )}
            </div>
            {FiltersPanel}
          </div>
        </aside>

        {/* Results */}
        <main>
          <div className="mb-5 flex items-baseline justify-between">
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight">{t("discover.title")}</h1>
              <h2 className="mt-1 text-sm text-muted-foreground" role="status" aria-live="polite">
                {loading ? t("discover.searching") : t("discover.spacesFound", { count: filtered.length })}
              </h2>
            </div>
            {!loading && filtered.length > 0 && (
              <p className="text-sm text-muted-foreground">{t("discover.updatedNow")}</p>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <SpaceSkeleton key={i} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState onReset={resetFilters} />
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.map((s) => (
                <SpaceCard key={s.id} space={s} />
              ))}
            </div>
          )}
        </main>
      </div>

      <SiteFooter />
    </div>
  );
}

function FilterSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function SpaceCard({ space }: { space: Space }) {
  const { t } = useTranslation();
  const { saved, toggle } = useFavorite(space.id);
  return (
    <article className="group overflow-hidden rounded-3xl border border-border bg-card shadow-soft transition-all hover:-translate-y-1 hover:shadow-elevated">
      <Link to="/spaces/$spaceId" params={{ spaceId: space.id }} className="block">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={space.img}
            alt={space.title}
            loading="lazy"
                decoding="async"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <button
            type="button"
            aria-label={saved ? t("discover.removeFavorite") : t("discover.saveFavorite")}
            aria-pressed={saved}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggle();
            }}
            className="absolute end-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-card/90 text-foreground shadow-soft backdrop-blur transition-colors hover:text-primary"
          >
            <Heart className={cn("h-4 w-4 transition-colors", saved && "fill-primary text-primary")} />
          </button>
          <span className="absolute start-3 top-3 rounded-full bg-card/95 px-2.5 py-1 text-[11px] font-semibold text-foreground shadow-soft backdrop-blur">
            {t(`discover.types.${space.type}`)}
          </span>
          {space.subscription && (
            <span
              title={t("discover.subscriptionBadge")}
              className="absolute bottom-3 start-3 inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground shadow-soft"
            >
              <BadgeCheck className="h-3.5 w-3.5" /> {t("discover.subscriptionBadge")}
            </span>
          )}
        </div>

        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-display text-base font-semibold leading-snug">{space.title}</h3>
            <div className="flex shrink-0 items-center gap-1 text-sm">
              <Star className="h-3.5 w-3.5 fill-foreground text-foreground" />
              <span className="font-medium">{space.rating}</span>
            </div>
          </div>

          <div className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" /> {t(`discover.cities.${space.city}`, { defaultValue: space.city })} · {space.reviews} {t("discover.reviewsSuffix")}
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {space.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-4 flex items-end justify-between border-t border-border pt-4">
            <div>
              <span className="font-display text-xl font-bold"><Riyal /> {space.price}</span>
              <span className="text-sm text-muted-foreground"> {t("discover.perHour")}</span>
            </div>
            <span className="rounded-full bg-[#5958F3] px-4 py-1.5 text-xs font-medium text-white">
              {t("discover.book")}
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}

function SpaceSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
      <Skeleton className="aspect-[4/3] w-full rounded-none" />
      <div className="space-y-3 p-5">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <div className="flex gap-2">
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-12 rounded-full" />
        </div>
        <div className="flex items-center justify-between border-t border-border pt-4">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-7 w-16 rounded-full" />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-border bg-surface px-6 py-20 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-card shadow-soft">
        <Inbox className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="mt-5 font-display text-xl font-semibold">{t("discover.emptyTitle")}</h3>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">
        {t("discover.emptyDesc")}
      </p>
      <Button onClick={onReset} className="mt-6 rounded-full">
        {t("discover.resetFilters")}
      </Button>
    </div>
  );
}

